import React, { useEffect, useState } from 'react';
import mammoth from 'mammoth';
import PDFRenderer from './PDFRenderer';
import HtmlGazeOverlay from './HtmlGazeOverlay';

const WORD_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const DocRenderer = ({ file, gazeEnabled = false, gazeKeywords = [], onGazeStats }) => {
  const [html, setHtml] = useState(null);   // DOCX converted to HTML
  const [text, setText] = useState(null);   // plain-text files
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fileType = file ? file.type : null;
  const isPdf = fileType === 'application/pdf';

  useEffect(() => {
    setHtml(null);
    setText(null);
    setError(null);

    if (!file) {
      setError('No file provided.');
      setLoading(false);
      return;
    }
    if (isPdf) {
      // PDFRenderer handles its own loading state.
      setLoading(false);
      return;
    }

    setLoading(true);
    if (WORD_TYPES.includes(fileType)) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer: event.target.result });
          setHtml(result.value);
        } catch {
          setError('Error converting Word file.');
        } finally {
          setLoading(false);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (fileType === 'text/plain') {
      const reader = new FileReader();
      reader.onload = (event) => {
        setText(event.target.result);
        setLoading(false);
      };
      reader.readAsText(file);
    } else {
      setLoading(false);
    }
  }, [file, fileType, isPdf]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <span>Loading...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500">
        {error}
      </div>
    );
  }

  let content;
  if (isPdf) {
    content = (
      <PDFRenderer
        file={file}
        gazeEnabled={gazeEnabled}
        gazeKeywords={gazeKeywords}
        onGazeStats={onGazeStats}
      />
    );
  } else if (html !== null) {
    content = (
      <HtmlGazeOverlay enabled={gazeEnabled} keywords={gazeKeywords} onStats={onGazeStats}>
        <div dangerouslySetInnerHTML={{ __html: html }} className="prose max-w-none" />
      </HtmlGazeOverlay>
    );
  } else if (text !== null) {
    content = (
      <HtmlGazeOverlay enabled={gazeEnabled} keywords={gazeKeywords} onStats={onGazeStats}>
        <pre className="whitespace-pre-wrap">{text}</pre>
      </HtmlGazeOverlay>
    );
  } else {
    content = <div>Unsupported file type.</div>;
  }

  return <div className="w-full border rounded">{content}</div>;
};

export default DocRenderer;
